import * as d3 from "d3";
import Utils from "../Utility/Utils";
import { AXIS_LABEL_SIZE, AXIS_POINT_SIZE } from "../constants";
import D3_Utility from "../Utility/D3_Utility";



export default class PolicyScoreGraph extends D3_Utility {

    constructor(chartArea, data, topCountries, distanceToFrontier) {
        super();
        this.MARGIN = { LEFT: 40, RIGHT: 0, TOP: 50, BOTTOM: 0 };
        this.WIDTH = chartArea.clientWidth;
        this.HEIGHT = chartArea.clientHeight;
        this.isThresholdHeight = window.innerHeight <= 800;

        this.cacheDOM();

        this.data = data;
        this.topCountries = topCountries
        this.rects = null
        this.legendHeight = null
        this.totalProductivity = 0;
        this.previousValues = {};
        this.distanceToFrontier = distanceToFrontier

        if (!Array.isArray(this.data)) {
            console.error('Invalid data format for dropdown population:', this.data);
            return;
        }

        this.shortCountryName = Utils.SHORT_COUNTRY_NAMES

        this.svg = d3
            .select("#policy-chart-area")
            .append("svg")
            .attr("width", this.WIDTH)
            .attr("height", this.HEIGHT - 15)

        this.updateGraph("Argentina");

    }

    cacheDOM() {
        this.dropdownElement = document.getElementById("selectCountry2");
        this.country = document.querySelector('#policy-simulator .titleBlock .main_title');
        this.countryFlag = document.querySelector('#policy-simulator .titleBlock img');
        this.baselinePPIPoint = document.querySelector('#policy-simulator .top-row .baselinePPI .point');
        this.baselinePPIDecimal = document.querySelector('#policy-simulator .top-row .baselinePPI .decimal');
        this.improvementScore = document.querySelector('#policy-simulator .top-row .policy_improvement .score');
        this.progressBar = document.querySelector('#policy-simulator .top-row .policy_improvement .progress-bar .progress');
        this.progressSVG = document.querySelector('#policy-simulator .top-row .policy_improvement .progress-score svg');
        this.progressThumb = document.querySelector('#policy-simulator .top-row .policy_improvement .progress-bar .thumb');
        this.productivityPPIPoint = document.querySelector('#policy-simulator .top-row .productivityPPI .point');
        this.productivityPPIDecimal = document.querySelector('#policy-simulator .top-row .productivityPPI .decimal');
        this.legendRow = document.querySelector('#policy-simulator .graph-area .legend-row');
        this.legendGridBox = document.querySelectorAll('#policy-simulator .graph-area .legend-row .legend-grid-box');
        this.countryImage = document.querySelector('#policy-simulator .bottom-row .img-block')
    }

    clearGraph() {
        this.svg.selectAll("*").remove();
        this.legendGridBox.forEach(box => {
            const selectElement = box.querySelector('select');
            if (selectElement) selectElement.remove();
        });
    }


    updateGraph(country) {
        this.clearGraph();
        this.getDTFs = PolicyScoreGraph.getTop3DTF(this.distanceToFrontier, country)


        const filteredData = this.data.filter(d => d.Country === country);
        const actualData = filteredData[0];
        this.dropdownElement.value = country

        // Reorder Categories
        const customOrder = ['Human', 'Physical', 'Innovation', 'Institution', 'Natural', 'Social'];
        const sortedCategories = actualData.Categories.sort((a, b) => {
            return customOrder.indexOf(a.group) - customOrder.indexOf(b.group);
        });


        // HANDLE BOTTOM GRAPH
        if (!actualData || !actualData.Categories) {
            console.error("No data available for the selected country:", country);
            return;
        }

        this.updateHeader(actualData)
        const totalSegments = actualData.Categories.length; // 6
        const totalWidth = this.WIDTH - this.MARGIN.LEFT - this.MARGIN.RIGHT;


        const { adjustedSmallerWidth, largerWidth } = PolicyScoreGraph.calculateWidths(country, totalWidth, totalSegments, this.distanceToFrontier);

        if (adjustedSmallerWidth === undefined || largerWidth === undefined) {
            console.error('Width calculations failed. Adjusted smaller width or larger width is undefined.');
            return;
        }

        let xPosition = this.MARGIN.LEFT + 15;
        let xPosition2 = this.MARGIN.LEFT + 15;
        let xPosition3 = this.MARGIN.LEFT + 15;

        this.legendHeight = this.legendRow ? this.legendRow.getBoundingClientRect().height : 0;

        const minValue = d3.min(actualData.Categories, d => d.value)
        const yScale = this.create_Y_Scale({
            minValue: (minValue - 0.5),
            maxValue: d3.max(actualData.Categories, d => d.value),
            rangeMin: (this.HEIGHT - 15),
            rangeMax: (this.MARGIN.TOP + this.legendHeight),
        });


        // Draw the axis and lines
        this.drawYAxis(yScale);

        const baseline = yScale(0);
        // Legend Background
        const legendBg = this.svg
            .append("g")
            .selectAll("rect")
            .data(actualData.Categories)
            .join("rect")
            .attr("x", (d, i) => {
                const currentX = xPosition;
                const currentWidth = this.getDTFs.some(item => item.group === d.group) ? largerWidth : adjustedSmallerWidth;
                xPosition += currentWidth; // Increment x position for the next rectangle
                return currentX;
            })
            .attr("y", 0)
            .attr("width", d => (this.getDTFs.some(item => item.group === d.group) ? largerWidth : adjustedSmallerWidth))
            .attr("height", this.legendHeight)
            .attr("fill", d => (this.getDTFs.some(item => item.group === d.group) && (country !== 'Luxembourg' || d.group !== 'Human'))
                ? "#fff"
                : "rgba(255, 255, 255, 0.5)"
            )
            .attr("stroke", "rgba(225, 227, 228, 1)")
            .attr("stroke-dasharray", "5,10")
            .attr("stroke-width", 1)
            .attr("opacity", 1)

        // Width calculation for Upper Legends
        const totalLegendSegments = this.legendGridBox.length;
        const totalLegendWidth = this.legendRow.querySelector('.second-box').getBoundingClientRect().width;

        // Calculate dynamic widths
        const smallerLegendWidth = totalLegendWidth / (totalLegendSegments + 0.5); // Smaller width
        const largerLegendWidth = smallerLegendWidth * 1.5; // Larger width

        // Calculate total space required and adjust
        const totalLegendSmallerRects = actualData.Categories.filter(d => this.getDTFs.some(item => item.group === d.group)).length;
        const totalLegendLargerRects = totalLegendSegments - totalLegendSmallerRects;
        const adjustedLegendSmallerWidth = (totalLegendWidth - (totalLegendLargerRects * largerLegendWidth)) / totalLegendSmallerRects;

        actualData.Categories.forEach((d, i) => {
            const legendBox = this.legendGridBox[i];
            const mainScore = legendBox.querySelector('.main-score');
            const simulatedScore = legendBox.querySelector('.simulated-score');

            // Update the score text
            mainScore.textContent = `$${Utils.roundUpToDecimal(d.value, 1)}`;

            // Check if the category belongs to a group present in 'this.getDTFs'
            if (this.getDTFs.some(item => item.group === d.group)) {
                legendBox.style.width = `${largerLegendWidth}px`;

                // Skip if country is Luxembourg and the group is 'Human'
                if (country === 'Luxembourg' && d.group === 'Human') {
                    simulatedScore.style.display = "none";
                    return;
                } else {
                    simulatedScore.style.display = "block";
                    this.addCompareDropdown(d.group, i, country);

                    // Query the compareSelect element once
                    const compareSelect = document.querySelector(`#compareSelect_${d.group}`);
                    if (compareSelect) {
                        compareSelect.addEventListener('change', () => {
                            const group = compareSelect.dataset.group;
                            const newValue = compareSelect.value === 'null'
                                ? 0
                                : parseFloat(compareSelect.value.split(',')[1]) - parseFloat(Utils.roundUpToDecimal(d.value, 1));

                            // Update total productivity
                            if (this.previousValues[group] !== undefined) {
                                this.totalProductivity -= this.previousValues[group];
                            }
                            this.totalProductivity += newValue;
                            this.previousValues[group] = newValue;

                            this.updateHeader(actualData);

                            // Update the simulated score point display
                            const simulatedScorePoint = document.querySelector(`#policy-simulator [data-legend-group="${group}"] .simulated-score .point`);
                            simulatedScorePoint.textContent = compareSelect.value === 'null' ? 0 : compareSelect.value.split(',')[1];

                            // Reform the graph with the updated values
                            const allCompareDropdown = document.querySelectorAll('#policy-simulator .graph-area .compareSelect');
                            this.reformGraph(allCompareDropdown, actualData, compareSelect);
                        });
                    }
                }


            } else {
                legendBox.style.width = `${adjustedLegendSmallerWidth}px`;
                simulatedScore.style.display = "none";
            }
        });



        // Draw rectangles dynamically
        this.rects = this.svg
            .append("g")
            .selectAll("rect")
            .data(actualData.Categories)
            .join("rect")
            .attr('id', d => `${d.group}_rect`)
            .attr("x", (d, i) => {
                const currentX = xPosition2;
                const currentWidth = this.getDTFs.some(item => item.group === d.group) ? largerWidth : adjustedSmallerWidth;
                xPosition2 += currentWidth; // Increment x position for the next rectangle
                return currentX;
            })
            .attr("y", 0)
            .attr("width", d => (this.getDTFs.some(item => item.group === d.group) ? largerWidth : adjustedSmallerWidth))
            .attr("height", this.HEIGHT - 15)
            .attr("fill", d => (this.getDTFs.some(item => item.group === d.group) && (country !== 'Luxembourg' || d.group !== 'Human'))
                ? "rgba(179, 219, 254, 0.2)"
                : "none"
            )
            .attr("stroke", "rgba(225, 227, 228, 1)")
            .attr("stroke-dasharray", "5,10")
            .attr("stroke-width", 1)
            .attr("opacity", 1)

        // Actual Data Bars
        this.svg
            .append("g")
            .selectAll("rect")
            .data(actualData.Categories)
            .join("rect")
            .attr('class', 'data-rect')
            .attr("x", (d, i) => {
                const bgRectsArr = this.rects._groups[0];
                const boxCenter = bgRectsArr[i].getBBox().width / 2;
                return bgRectsArr[i].getBBox().x + boxCenter - 7.5;
            })
            .attr("data-label-rect", d => d != null ? d.group : "")
            .attr("y", baseline) // Start at the baseline (0)
            .attr("height", 0) // Initial height is 0 for the animation 
            .attr("width", 15)
            .attr("fill", d => (this.getDTFs.some(item => item.group === d.group) && (country !== 'Luxembourg' || d.group !== 'Human'))
                ? "none"
                : "rgba(225, 225, 225, 1)"
            )
            .attr("opacity", 1)
            .transition()
            .duration(500)
            .ease(d3.easePoly.exponent(1))
            .attr("y", d => d?.value >= 0 ? yScale(d.value) : baseline) // Adjust for negative values
            .attr("height", d => Math.abs(yScale(d.value) - baseline));


        // this.svg.append("g")
        //     .selectAll("text")
        //     .data(actualData.Categories)
        //     .join("text")
        //     .attr('x', (d, i) => {
        //         const bgRectsArr = this.rects._groups[0]
        //             const boxCenter = bgRectsArr[i].getBBox().width / 2

        //             return bgRectsArr[i].getBBox().x + boxCenter
        //     })
        //     .attr('y', d => (d ? yScale(d.value) : baseline))
        //     .text(d => d ? d.group : '')

        const whiteGradient = this.svg.append("defs")
            .append("linearGradient")
            .attr("id", "whiteGradient")
            .attr("x1", "0%")  // Start at the left side
            .attr("y1", "100%") // Start from the bottom (100% of the height)
            .attr("x2", "0%")  // End at the left side
            .attr("y2", "0%")  // End at the top (0% of the height)
            .selectAll("stop")
            .data([
                { offset: "0%", color: "white", opacity: 0 },
                { offset: "50%", color: "white", opacity: 0.7 },
                { offset: "100%", color: "white", opacity: 0 }
            ])
            .enter().append("stop")
            .attr("offset", d => d.offset)
            .attr("stop-color", d => d.color)
            .attr("stop-opacity", d => d.opacity);

        const normalGradient = this.svg.append("defs")
            .append("linearGradient")
            .attr("id", "normalGradient")
            .attr("x1", "0%")  // Start at the left side
            .attr("y1", "100%") // Start from the bottom (100% of the height)
            .attr("x2", "0%")  // End at the left side
            .attr("y2", "0%")  // End at the top (0% of the height)
            .selectAll("stop")
            .data([
                { offset: "0%", color: "#E6F3FF", opacity: 0 },
                { offset: "50%", color: "#E6F3FF", opacity: 1 },
                { offset: "100%", color: "#E6F3FF", opacity: 0 }
            ])
            .enter().append("stop")
            .attr("offset", d => d.offset)
            .attr("stop-color", d => d.color)
            .attr("stop-opacity", d => d.opacity);

        this.gradientRects = this.svg
            .append("g")
            .selectAll("rect")
            .data(actualData.Categories)
            .join("rect")
            .attr("x", (d, i) => {
                const currentX = xPosition3;
                const currentWidth = this.getDTFs.some(item => item.group === d.group) ? largerWidth : adjustedSmallerWidth;
                xPosition3 += currentWidth;
                return currentX;
            })
            .attr("data-label", d => d != null ? d.group : "")
            .attr("y", baseline > this.HEIGHT - this.MARGIN.BOTTOM - 65 + 25 ? this.HEIGHT - this.MARGIN.BOTTOM - 65 + 25 : baseline - 25) // Start at the baseline (0)
            .attr("height", this.isThresholdHeight ? 30 : 50) // Initial height is 0 for the animation
            .attr("width", d => (this.getDTFs.some(item => item.group === d.group) ? largerWidth : adjustedSmallerWidth))
            .attr("fill", d => (this.getDTFs.some(item => item.group === d.group) && (country !== 'Luxembourg' || d.group !== 'Human'))
                ? "url(#whiteGradient)"
                : "url(#normalGradient)"
            )
            .attr("opacity", 1)


        this.xLine = this.svg
            .append("line")
            .attr("x1", this.MARGIN.LEFT)
            .attr("x2", this.WIDTH)
            .attr("y1", baseline > this.HEIGHT - 15 ? this.HEIGHT - 15 : baseline)
            .attr("y2", baseline > this.HEIGHT - 15 ? this.HEIGHT - 15 : baseline)
            .attr("stroke", "#000")
            .attr("stroke-width", 1);

        this.svg
            .append("g")
            .selectAll("circle")
            .data(actualData.Categories)
            .join("circle")
            .attr("class", "data-circle")
            .attr("cx",
                (d, i) => {
                    const bgRectsArr = this.rects._groups[0]
                    const boxCenter = bgRectsArr[i].getBBox().width / 2

                    return bgRectsArr[i].getBBox().x + boxCenter

                }
            )
            .attr("cy", baseline) // Start at the baseline
            .attr("r", 8) // Initial radius is 0 for the animation
            .attr("fill", d => (this.getDTFs.some(item => item.group === d.group) && (country !== 'Luxembourg' || d.group !== 'Human'))
                ? "#fff"
                : "rgba(0,0,0,0.1)"
            )
            .attr("stroke", d => (this.getDTFs.some(item => item.group === d.group) && (country !== 'Luxembourg' || d.group !== 'Human'))
                ? "#000"
                : "rgba(0,0,0,0.3)"
            )
            .attr("stroke-width", 2)
            .transition() // Apply the animation
            .duration(500) // Animation duration in milliseconds
            .ease(d3.easePoly.exponent(1)) // Optional easing function
            .attr("cy", d => (d ? yScale(d.value) : baseline))

        this.svg.append("g")
            .selectAll("text")
            .data(actualData.Categories)
            .join("text")
            .attr("class", "data-text")
            .attr("x", (d, i) => {
                const bgRectsArr = this.rects._groups[0]
                const boxCenter = bgRectsArr[i].getBBox().width / 2
                return bgRectsArr[i].getBBox().x + boxCenter
            })
            .attr("y", baseline)
            .attr("dy", 0)
            .attr("dx", -25)
            .attr("opacity", 0)
            .attr("fill", "rgba(2, 81, 151, 1)")
            .attr("text-anchor", "middle")
            .attr("font-size", AXIS_LABEL_SIZE + 'px')
            .attr("font-family", "Logical")
            .text(d => (d && d.value !== undefined) ? Utils.roundUpToDecimal(d.value, 1) : '')
            .transition()
            .duration(500)
            .ease(d3.easePoly.exponent(1))
            .attr("y", d => (d && d.value !== undefined) ? yScale(d.value) : baseline)
            .attr("dy", -5);

        this.svg.selectAll(".data-circle")
            .on("mouseenter", function (event, d) {
                d3.selectAll(".data-text").filter((data) => data === d)
                    .transition()
                    .duration(200)
                    .attr("opacity", 1);
            })
            .on("mouseleave", function (event, d) {
                d3.selectAll(".data-text").filter((data) => data === d)
                    .transition()
                    .duration(200)
                    .attr("opacity", 0);
            });

        // Description Text
        const infoText = this.svg.append("text")
            .attr('class', 'info-text')
            .text('Potential productivity value (in US dollars)')
            .attr("font-size", AXIS_LABEL_SIZE + 'px')
            .style('transform', 'rotate(-90deg)')
            .attr('y', 20)
            .attr('x', -this.HEIGHT + 15)
            .attr('fill', 'rgba(0,0,0,0.8)')
    }



    updateHeader(actualData) {

        if (this.countryFlag && this.country) {
            this.countryFlag.src = `assets/images/Flags/${actualData.Flag}`;
            this.country.textContent = this.shortCountryName[actualData.Country] || actualData.Country;
        }


        const baselinePPI = actualData.Categories.reduce((acc, cum) => acc + cum.value, 0);
        if (this.baselinePPIPoint && this.baselinePPIDecimal) {
            this.baselinePPIPoint.textContent = parseInt(Utils.roundUpToDecimal(baselinePPI, 1));
            this.baselinePPIDecimal.textContent = `.${Utils.extractDecimalDigit(baselinePPI, 1)}`;
        }

        const finalScore = baselinePPI + parseFloat(Utils.roundUpToDecimal(this.totalProductivity, 1))
        if (this.productivityPPIPoint && this.productivityPPIDecimal) {
            this.productivityPPIPoint.textContent = parseInt(Utils.roundUpToDecimal(finalScore, 1))
            this.productivityPPIDecimal.textContent = `.${Utils.extractDecimalDigit(finalScore, 1)}`;
        }

        if (parseFloat(Utils.roundUpToDecimal(this.totalProductivity, 1)) < 0) {
            this.progressSVG.classList.add('negative')
        } else {
            this.progressSVG.classList.remove('negative')
        }

        if (this.improvementScore) {
            // this.improvementScore.textContent = `$${Utils.truncateToDecimals(Utils.calculatePercentageDifference(finalScore, baselinePPI), 1)}%`
            this.improvementScore.textContent = `$${0 + parseFloat(Utils.roundUpToDecimal(this.totalProductivity, 1))}`
            // this.progressBar.style.width = `${Utils.truncateToDecimals(Utils.calculatePercentageDifference(finalScore, baselinePPI), 1)}%`
            // this.progressThumb.style.left = `${Utils.truncateToDecimals(Utils.calculatePercentageDifference(finalScore, baselinePPI), 1)}%`
        }

        const regionEncoded = encodeURIComponent(actualData.Region);
        const countryEncoded = encodeURIComponent(actualData.Country);

        this.countryImage.style.backgroundImage = `url(assets/images/country_images/${regionEncoded}/${countryEncoded}.webp)`
    }

    static calculateWidths(country, totalWidth, totalSegments, distanceToFrontier) {
        if (totalSegments === 0) {
            return { adjustedSmallerWidth: undefined, largerWidth: undefined };
        }

        const smallerWidth = totalWidth / (totalSegments + 0.5);
        const largerWidth = smallerWidth * 1.5;

        const totalSmallerRects = PolicyScoreGraph.getTop3DTF(distanceToFrontier, country).length;
        const totalLargerRects = totalSegments - totalSmallerRects;

        // Ensure no division by zero  
        if (totalSmallerRects === 0) {
            return { adjustedSmallerWidth: largerWidth, largerWidth };
        }

        const adjustedSmallerWidth = (totalWidth - (totalLargerRects * largerWidth)) / totalSmallerRects;

        return { adjustedSmallerWidth, largerWidth };
    }


    // Get All DTF's Of a Country
    static getCountryDTF(countriesDTF, countryName) {
        // Find the country data in the DTF results
        const countryDTF = countriesDTF.find(country => country.country === countryName);

        if (!countryDTF) {
            return `Country ${countryName} not found`;
        }

        return countryDTF.DTF; // Return DTF for the country
    }

    // Get Top 3 DTF's Of a Country
    static getTop3DTF(countriesDTF, countryName) {
        // Find the country data in the DTF results
        const countryDTF = countriesDTF.find(country => country.country === countryName);
        if (!countryDTF) {
            return `Country ${countryName} not found`;
        }

        // Sort the DTF categories in descending order based on DTF values
        const sortedDTF = countryDTF.DTF.sort((a, b) => b.DTF - a.DTF);

        // Return the top 3 categories
        return sortedDTF.slice(0, 3);
    }

    drawYAxis(yScale) {
        this.svg.selectAll('.y-axis').remove()
        this.svg.selectAll('.y-axis-line').remove()

        this.create_Y_Axis({
            svg: this.svg,
            className: "y-axis",
            translateX: this.MARGIN.LEFT,
            translateY: 0,
            callInput: d3.axisLeft(yScale).ticks(5).tickSize(0).tickPadding(0),
            data: yScale.ticks(5),
            width: this.WIDTH,
            y1: d => yScale(d),
            y2: d => yScale(d),
            strokeColor: "rgba(225, 227, 228, 1)",
            strokeWidth: 1,
        })

        // this.svg.append("g")
        //     .attr("class", "y-axis")
        //     .attr("transform", `translate(${this.MARGIN.LEFT}, 0)`)
        //     .call(d3.axisLeft(yScale).ticks(5).tickSize(0).tickPadding(0))
        //     .selectAll("path, line, text")
        //     .style('font-size', AXIS_LABEL_SIZE)
        //     .style("stroke", "none");

        this.svg.append("g")
            .selectAll("line")
            .data(yScale.ticks(5))
            .join("line")
            .attr('class', 'y-axis-line')
            .attr("x1", this.MARGIN.LEFT)
            .attr("x2", this.WIDTH)
            .attr("y1", d => yScale(d))
            .attr("y2", d => yScale(d))
            .attr("stroke", "rgba(225, 227, 228, 1)")
            .attr("stroke-dasharray", "2,2")
            .attr("stroke-width", 1);
    }

    addCompareDropdown(data, index, currentCountry) {
        const selectDropdown = document.createElement('select')
        selectDropdown.setAttribute('class', 'compareSelect')
        selectDropdown.setAttribute('id', 'compareSelect_' + data)
        selectDropdown.setAttribute('data-group', data)
        const option = document.createElement('option');
        option.setAttribute('selected', true)
        option.textContent = 'Select country'
        option.value = null
        selectDropdown.appendChild(option);

        this.topCountries.forEach(d => {
            if (d.group == data) {
                d.topCountries.forEach(country => {
                    if (currentCountry == 'Luxembourg' && country.country == 'Norway') {
                        return
                    }
                    if (country.country != currentCountry) {
                        const option = document.createElement('option');
                        option.value = [country.country, Utils.roundUpToDecimal(country.sum, 1)];
                        option.textContent = country.country;
                        selectDropdown.appendChild(option);
                    }
                })
            }
        })



        this.legendGridBox[index].appendChild(selectDropdown)
    }



    reformGraph(allCompareDropdown, actualData, thisDropdown) {
        const compareDropdownArr = [];
        allCompareDropdown.forEach(dropdown => {
            if (dropdown.value !== 'null') {
                const val = dropdown.value.split(',');
                compareDropdownArr.push({ group: dropdown.dataset.group, country: val[0], value: parseFloat(val[1]) });
            } else {
                compareDropdownArr.push({ group: dropdown.dataset.group, country: null, value: null });
            }
        });



        const thisGroup = thisDropdown.dataset.group

        // Handle the case where there's no actual data or ranked segments
        if (!actualData || !actualData.Categories) {
            console.error("No data available for the selected country.");
            return;
        }

        // Find the maximum value from the original data
        const maxOriginalValueObj = actualData.Categories.reduce((max, current) => {
            return (current.value > max.value) ? current : max;
        }, actualData.Categories[0]);

        // Find the maximum value from the comparison data
        const maxNewValueObj = compareDropdownArr.reduce((max, current) => {
            return (current.value > max.value) ? current : max;
        }, compareDropdownArr[0]);

        // Get the overall maximum value
        const maxValueObj = maxOriginalValueObj.value > maxNewValueObj.value ? maxOriginalValueObj : maxNewValueObj;
        const maxValue = maxValueObj.value;

        // Create the updated yScale to account for the new max value
        const minValue = d3.min(actualData.Categories, d => d.value)
        const yScale = d3.scaleLinear()
            .domain([minValue - 0.5, maxValue])
            .range([this.HEIGHT - 15, this.MARGIN.TOP + this.legendHeight]);

        // Redraw the y-axis with the updated yScale
        this.drawYAxis(yScale);

        // Remove previous bars before adding the new ones
        this.removeSVGElements()

        // Redraw the bars with the new scale and values
        const grayRecyGradient = this.svg.append("defs")
            .append("linearGradient")
            .attr("id", "grayRecyGradient")
            .attr("x1", "0%")  // Start at the left side
            .attr("y1", "100%") // Start from the bottom (100% of the height)
            .attr("x2", "0%")  // End at the left side
            .attr("y2", "0%")  // End at the top (0% of the height)
            .selectAll("stop")
            .data([
                { offset: "0%", color: "rgba(225, 225, 225, 1)", opacity: 0 }, // White at the bottom
                { offset: "50%", color: "rgba(225, 225, 225, 1)", opacity: 0.8 }, // White at the bottom
                { offset: "100%", color: "rgba(225, 225, 225, 1)", opacity: 1 } // Transparent at the top
            ])
            .enter().append("stop")
            .attr("offset", d => d.offset)
            .attr("stop-color", d => d.color)
            .attr("stop-opacity", d => d.opacity);

        this.svg
            .append("g")
            .attr('class', 'expanded-data-rect-group')
            .selectAll("rect")
            .data(actualData.Categories)
            .join("rect")
            .attr('class', 'expanded-data-rect')
            .attr("x", (d, i) => {
                const bgRectsArr = this.rects._groups[0]; // Access the background rectangles
                const boxCenter = bgRectsArr[i].getBBox().width / 2;
                return bgRectsArr[i].getBBox().x + boxCenter - 7.5; // Center the new bar horizontally
            })
            .attr("data-label-rect", d => d != null ? d.group : "")
            .attr("y", yScale(0)) // Start at the baseline (0) 
            .attr("height", 0) // Initial height is 0 for the animation
            .attr("width", 15)
            .attr("fill", d => this.getDTFs.some(item => item.group === d.group) ? "none" : "url(#grayRecyGradient)")
            .attr("opacity", 1)
            .transition()
            .duration(500)
            .ease(d3.easePoly.exponent(1)) // Apply easing to the transition
            .attr("y", d => d?.value >= 0 ? yScale(d.value) : yScale(0)) // Adjust for negative values
            .attr("height", d => Math.abs(yScale(d.value) - yScale(0)))

        const rectGradient = this.svg.append("defs")
            .attr('class', 'expanded-defs')
            .append("linearGradient")
            .attr("id", "rectGradient")
            .attr("x1", "0%")  // Start at the left side
            .attr("y1", "100%") // Start from the bottom (100% of the height)
            .attr("x2", "0%")  // End at the left side
            .attr("y2", "0%")  // End at the top (0% of the height)
            .selectAll("stop")
            .data([
                { offset: "0%", color: "rgba(179, 219, 254, 1)", opacity: 0 }, // White at the bottom
                { offset: "100%", color: "rgba(179, 219, 254, 1)", opacity: 1 } // Transparent at the top
            ])
            .enter().append("stop")
            .attr("offset", d => d.offset)
            .attr("stop-color", d => d.color)
            .attr("stop-opacity", d => d.opacity);

        this.svg
            .append("g")
            .attr('class', 'expanded-circle-group')
            .selectAll("circle")
            .data(actualData.Categories)
            .join("circle")
            .attr("class", "data-circle")
            .attr("cx", (d, i) => {
                const bgRectsArr = this.rects._groups[0];
                const boxCenter = bgRectsArr[i].getBBox().width / 2;
                return bgRectsArr[i].getBBox().x + boxCenter;
            })
            .attr("cy", yScale(0)) // Start at the baseline (0)
            .attr("r", 8) // Initial radius is 0 for the animation
            .attr("fill", d => {
                const match = compareDropdownArr.find(obj => obj.group === d.group);
                return match ? "#fff" : "rgba(0,0,0,0.1)";
            })
            .attr("stroke", "rgba(0,0,0,0.3)") // Stroke color for the circles
            .attr("stroke-width", 2)
            .style("position", "relative")
            .style("z-index", 45)  // Ensure the circles are above bars
            .transition() // Apply the animation
            .duration(500) // Animation duration in milliseconds
            .ease(d3.easePoly.exponent(1)) // Optional easing function
            .attr("cy", d => (d ? yScale(d.value) : yScale(0)))

        this.svg.append("g")
            .attr("class", "expanded-data-text")
            .selectAll("text")
            .data(actualData.Categories)
            .join("text")
            .attr("class", "data-text")
            .attr("x", (d, i) => {
                const bgRectsArr = this.rects._groups[0];
                const boxCenter = bgRectsArr[i].getBBox().width / 2;
                return bgRectsArr[i].getBBox().x + boxCenter;
            })
            .attr("y", yScale(0))
            .attr("dy", 0)
            .attr("dx", -25)
            .attr("opacity", 0)
            .attr("fill", "rgba(2, 81, 151, 1)")
            .attr("text-anchor", "middle")
            .attr("font-size", AXIS_LABEL_SIZE + 'px')
            .attr("font-family", "Logical")
            .text(d => (d && d.value !== undefined) ? Utils.roundUpToDecimal(d.value, 1) : '')
            .transition()
            .duration(500)
            .ease(d3.easePoly.exponent(1))
            .attr("y", d => (d && d.value !== undefined) ? yScale(d.value) : yScale(0))
            .attr("dy", -5);

        this.svg.selectAll(".data-circle")
            .on("mouseenter", function (event, d) {
                d3.selectAll(".data-text").filter((data) => data === d)
                    .transition()
                    .duration(200)
                    .attr("opacity", 1);
            })
            .on("mouseleave", function (event, d) {
                d3.selectAll(".data-text").filter((data) => data === d)
                    .transition()
                    .duration(200)
                    .attr("opacity", 0);
            });

        this.xLine
            .attr("y1", yScale(0) > this.HEIGHT - 15 ? this.HEIGHT - 15 : yScale(0))
            .attr("y2", yScale(0) > this.HEIGHT - 15 ? this.HEIGHT - 15 : yScale(0))

        this.gradientRects
            .attr("y", yScale(0) > this.HEIGHT - this.MARGIN.BOTTOM - 65 + 25 ? this.HEIGHT - this.MARGIN.BOTTOM - 65 + 25 : yScale(0) - 25)

        setTimeout(() => {
            compareDropdownArr.forEach(ar => {
                if (ar.value) {
                    const floatValue = ar.value;

                    const previousBar = this.svg.select(`rect[data-label-rect="${ar.group}"]`)._groups[0][0];
                    const previousBarBBox = previousBar.getBBox();

                    const previousBarData = actualData.Categories.find(obj => obj.group === ar.group)

                    const startY = yScale(previousBarData.value);
                    const finalY = yScale(floatValue);
                    const barHeight = Math.abs(finalY - startY);


                    const barDiff = Utils.roundUpToDecimal(floatValue - parseFloat(Utils.roundUpToDecimal(previousBarData.value, 1)), 1)

                    // EXPANDED DATA BAR
                    this.svg.append("rect")
                        .attr('id', `${ar.group}_bar`)
                        .attr('width', 15)
                        .attr('x', () => {
                            const boxCenter = previousBarBBox.width / 2;
                            return previousBarBBox.x + boxCenter - 7.5;
                        })
                        .attr('class', 'expanded-bar')
                        .attr('fill', "url(#rectGradient)")
                        .style("transform-origin", "bottom")
                        .attr('y', startY)
                        .attr('height', 0)
                        .style("position", "relative")
                        .style("z-index", 33)
                        .transition()
                        .duration(500)
                        .ease(d3.easePoly.exponent(1))
                        .attr('y', finalY)
                        .attr('height', barHeight);

                    // EXPANDED DATA CIRCLE
                    this.svg
                        .append("circle")
                        .attr('class', 'expanded-circle')
                        .attr("cx", (d, i) => {
                            const boxCenter = previousBarBBox.width / 2;
                            return previousBarBBox.x + boxCenter;
                        })
                        .attr("cy", startY)
                        .attr("r", 8)
                        .attr("fill", "rgba(104, 183, 253, 1)")
                        .attr("stroke", "rgba(2, 108, 202, 1)")
                        .attr("stroke-width", 2)
                        .style("position", "relative")
                        .style("z-index", 44)
                        .transition()
                        .duration(500)
                        .ease(d3.easePoly.exponent(1))
                        .attr("cy", finalY);

                    // EXPANDED DATA LABEL 
                    this.svg.append("text")
                        .attr("class", "expanded-text")
                        .attr("x", (d, i) => {
                            const boxCenter = previousBarBBox.width / 2;
                            return previousBarBBox.x + boxCenter;
                        })
                        .attr("y", startY)
                        .attr("dy", 0)
                        .attr("dx", -25)
                        .attr("fill", "rgba(2, 81, 151, 1)")
                        .attr("text-anchor", "middle")
                        .attr("font-size", AXIS_LABEL_SIZE + 'px')
                        .attr("font-family", "Logical")
                        .text(floatValue)
                        .transition()
                        .duration(500)
                        .ease(d3.easePoly.exponent(1))
                        .attr("y", finalY)
                        .attr("dy", 5);

                    // EXPANDED WHITE CONTEXT BG
                    const whiteBG = this.svg.append("rect")
                        .attr('id', `${ar.group}_content_BG`)
                        .attr('width', (this.WIDTH / 10) / 2)
                        .attr('x', () => {
                            const boxCenter = previousBarBBox.width / 2;
                            return previousBarBBox.x + boxCenter + 15;
                        })
                        .attr('class', 'expanded-content_BG')
                        .attr('fill', "#fff")
                        .style("transform-origin", "bottom")
                        .attr('y', startY)
                        .attr('height', 0)
                        .style("position", "relative")
                        .style("z-index", 33)
                        .transition()
                        .duration(500)
                        .ease(d3.easePoly.exponent(1))
                        .attr('y', () => {
                            if (parseFloat(Utils.roundUpToDecimal(this.totalProductivity)) < 0) {
                                return startY
                            } else {
                                return finalY
                            }
                        })
                        .attr('height', barHeight);


                    // EXPANDED CONTEXT TEXT
                    const textElement = this.svg.append("text")
                        .attr('id', `${ar.group}_double_line_text`)
                        .attr('class', 'double_line_text')
                        .attr("x", () => {
                            const boxCenter = previousBarBBox.width / 2;
                            return previousBarBBox.x + boxCenter + 20;
                        })
                        .attr("y", previousBarBBox.y)
                        .attr("dy", 0)
                        .attr("text-anchor", "left")
                        .attr("font-size", AXIS_POINT_SIZE + 'px')
                        .attr("font-family", "'Logical', Arial, sans-serif")
                        .attr("fill", "rgba(2, 108, 202, 1)")
                        .style("z-index", 32)
                        .style("position", "relative")
                        .style("opacity", 1);

                    textElement.append("tspan")
                        .attr("x", () => {
                            const boxCenter = previousBarBBox.width / 2;
                            return previousBarBBox.x + boxCenter + 20;
                        })
                        .attr("dy", 0)
                        .text("Distance");

                    textElement.append("tspan")
                        .attr("x", () => {
                            const boxCenter = previousBarBBox.width / 2;
                            return previousBarBBox.x + boxCenter + 20;
                        })
                        .attr("dy", "1.2em")
                        .text("to frontier");

                    textElement.transition()
                        .duration(500)
                        .ease(d3.easePoly.exponent(1))
                        .style("opacity", 1);

                    const yPos = finalY + ((startY - finalY) / 2)

                    textElement.transition()
                        .duration(500)
                        .ease(d3.easePoly.exponent(1))
                        .attr("y", finalY + ((startY - finalY) / 2) - (yPos > this.HEIGHT - 45 ? 50 : 15))

                    // DIFFRENCE VALUE
                    this.svg.append("text")
                        .attr("class", "expanded-text")
                        .attr("x", () => {
                            const boxCenter = previousBarBBox.width / 2;
                            return previousBarBBox.x + boxCenter + 20;
                        })
                        .attr("y", previousBarBBox.y)
                        .attr("fill", "rgba(2, 108, 202, 1)")
                        .attr("text-anchor", "left")
                        .attr("font-size", `${AXIS_LABEL_SIZE + 2}px`)
                        .attr("font-family", "Arial")
                        .text(`$${barDiff}`)
                        .attr("opacity", 0)
                        .transition()
                        .duration(500)
                        .ease(d3.easePoly.exponent(1))
                        .attr("opacity", 1)
                        .attr("y", finalY + ((startY - finalY) / 2) + (yPos > this.HEIGHT - 45 ? -15 : 18))

                    // TOP CHEVRON
                    this.svg.append("polygon")
                        .attr('id', `${ar.group}_chevron`)
                        .attr('points', (d) => {
                            const boxCenter = previousBarBBox.width / 2;
                            const rectX = previousBarBBox.x + boxCenter + 12.5;
                            const rectY = previousBarBBox.y + 5;  // Start position
                            const chevronHeight = 5; // Height of the chevron (triangle)

                            // Define points for the chevron shape
                            const points = [
                                [rectX, rectY], // Left corner of the chevron (start)
                                [rectX + 2.5, rectY - chevronHeight], // Peak of the chevron
                                [rectX + 5, rectY], // Right corner of the chevron (end)
                            ];

                            return points.map(d => d.join(",")).join(" ");
                        })
                        .attr('class', 'expanded-chevron')
                        .attr('fill', "rgba(33, 64, 92, 1)")
                        .style("z-index", 34)  // Ensure the chevron is just above the rectangle
                        .style("position", "relative")
                        .transition()
                        .duration(500)
                        .ease(d3.easePoly.exponent(1))
                        .attr('points', (d) => {
                            const boxCenter = previousBarBBox.width / 2;
                            const rectX = previousBarBBox.x + boxCenter + 12.5;
                            const rectY = finalY + 5;  // Start position
                            const chevronHeight = 5; // Height of the chevron (triangle)

                            // Define points for the chevron shape
                            const points = [
                                [rectX, rectY], // Left corner of the chevron (start)
                                [rectX + 2.5, rectY - chevronHeight], // Peak of the chevron
                                [rectX + 5, rectY], // Right corner of the chevron (end)
                            ];

                            return points.map(d => d.join(",")).join(" ");
                        });

                    // BOTTOM CHEVRON
                    this.svg.append("polygon")
                        .attr('id', `${ar.group}_chevron`)
                        .attr('points', (d) => {
                            const boxCenter = previousBarBBox.width / 2;
                            const rectX = previousBarBBox.x + boxCenter + 12.5;
                            const rectY = startY - 5;  // Start position
                            const chevronHeight = -5; // Height of the chevron (triangle)

                            // Define points for the chevron shape
                            const points = [
                                [rectX, rectY], // Left corner of the chevron (start)
                                [rectX + 2.5, rectY - chevronHeight], // Peak of the chevron
                                [rectX + 5, rectY], // Right corner of the chevron (end)
                            ];

                            return points.map(d => d.join(",")).join(" ");
                        })
                        .attr('class', 'expanded-chevron')
                        .attr('fill', "rgba(33, 64, 92, 1)")
                        .style("z-index", 34)  // Ensure the chevron is just above the rectangle
                        .style("position", "relative")

                    // DOTTED CHEVRON LINE
                    this.svg.append("line")
                        .attr('id', `${ar.group}_dotted_line`)
                        .attr('x1', () => {
                            const boxCenter = previousBarBBox.width / 2;
                            return previousBarBBox.x + boxCenter + 15;  // Same position as the chevron
                        })
                        .attr('y1', startY)  // Start at the y position of the previous bar
                        .attr('x2', () => {
                            const boxCenter = previousBarBBox.width / 2;
                            return previousBarBBox.x + boxCenter + 15;  // Same position as the chevron 
                        })
                        .attr('class', 'expanded_dotted_line')
                        .attr('y2', startY)  // Start at the y position of the previous bar
                        .attr('stroke', "rgba(33, 64, 92, 1)")  // Set the line color
                        .attr('stroke-width', 1)  // Line thickness
                        .attr('stroke-dasharray', "2,2")  // Dotted line (2px dash, 2px gap)
                        .style("z-index", 33)  // Ensure the line is below the chevron and content
                        .style("position", "relative")
                        .transition()  // Start the transition
                        .duration(500)  // Duration of the animation
                        .ease(d3.easePoly.exponent(1))  // Easing function for smoothness
                        .attr('y2', finalY);

                }
            });


            // this.svg
            //     .append("g")
            //     .selectAll("text")
            //     .data(actualData.Categories)
            //     .join("text")
            //     .attr("class", "expanded-text")
            //     .attr("x", (d, i) => {
            //         const bgRectsArr = this.rects._groups[0];
            //         const boxCenter = bgRectsArr[i].getBBox().width / 2;
            //         return bgRectsArr[i].getBBox().x + boxCenter;
            //     })
            //     .attr("y", d => (d ? yScale(d.value) - 10 : yScale(0) - 10))
            //     .attr("dy", 5)
            //     .attr("dy", 0)
            //     .attr("dx", -25)
            //     .attr("fill", "rgba(103, 103, 103, 1)")
            //     .attr("text-anchor", "middle")
            //     .attr("font-size", AXIS_LABEL_SIZE + 'px')
            //     .attr("font-family", "Logical")
            //     .attr("opacity", 0)
            //     .text((d) => Utils.truncateToDecimals(d.value, 1))
            //     .transition()
            //     .duration(500)
            //     .ease(d3.easePoly.exponent(1))
            //     .attr("opacity", (d) => d.rank >= 4 ? 1 : 0)

        }, 500)

    }


    resetGraph() {
        // Select all elements with the class 'compareSelect'
        const dropdowns = document.querySelectorAll('.compareSelect');

        // Check if any dropdowns are found
        if (dropdowns.length === 0) {
            console.warn('No dropdowns found to reset.');
            return;
        }

        // Iterate over each dropdown and reset its value
        dropdowns.forEach(dropdown => {
            dropdown.value = 'null';  // Reset dropdown value
        });
        this.removeSVGElements()
        document.querySelectorAll(`#policy-simulator .simulated-score .point`).forEach(point => point.textContent = 0)
        this.totalProductivity = 0
        this.previousValues = {}
        this.updateGraph(this.dropdownElement.value)

    }

    removeSVGElements() {
        this.svg.selectAll('.data-rect').remove();
        this.svg.selectAll('.data-text').remove();
        this.svg.selectAll('.data-circle').remove();

        this.svg.selectAll('.expanded-data-rect-group').remove();
        this.svg.selectAll('.expanded-circle-group').remove();
        this.svg.selectAll('.expanded-defs').remove();
        this.svg.selectAll('.expanded-data-text').remove();


        this.svg.selectAll('.expanded-bar').remove();
        this.svg.selectAll('.expanded-circle').remove();
        this.svg.selectAll('.expanded-text').remove();
        this.svg.selectAll('.expanded-content_BG').remove();
        this.svg.selectAll('.expanded-chevron').remove();
        this.svg.selectAll('.expanded_dotted_line').remove();
        this.svg.selectAll('.double_line_text').remove();
    }


}
